import { combineReducers, compose, createStore, applyMiddleware } from 'redux';
import routerMiddleware from '@/domain/router/redux/routerMiddleware';
import routeCompanyMiddleware, {
  companyIdOf,
  subscribeRouteCompany,
} from '@/domain/router/redux/routeCompanyMiddleware';
import { RouterFactory } from '@/domain/router/RouterModel';
import deferredToastMiddleware from '@/domain/ui/deferredToastMiddleware';
import chatMiddleware from '@/domain/chat/legacy/middleware';
import chatV2ToastMiddleware from '@/domain/chat/modern/middleware';
import Immutable from 'immutable';
import installDevTools from 'immutable-devtools';
import createSagaMiddleware, { END } from 'redux-saga';
import Reactotron from 'reactotron-react-js';
import Cookies from 'js-cookie';

import type { createBrowserRouter } from 'react-router-dom';

import { storage } from '@/lib/storage';
import { setUser, sagaUncaughtErrorHandler } from '@/lib/errorLogger';
import { DEFAULT_PANEL_WIDTH } from '@/domain/env/constants';

const __DEV__ = process.env.NODE_ENV === 'development';

installDevTools(Immutable);

const st = storage();
const reactotronEnabled = st.reactotronEnabled().get();
const env = {
  // to be removed
  dokkaToken: st.token().get(),
  chatToken: st.chatToken().get(),
  chatUser: st.chatUser().get(),
  userId: st.userId().get(),
  isDokkaSupport: st.isDokkaSupport().get() === 'true',
};

const locales = Cookies.get('locale') || '{}';
setUser(st.userGUID().get(), st.userId().get());

const linkedSidebarWidth = window.localStorage.getItem('dokkaLinkedSidebarWidth') || DEFAULT_PANEL_WIDTH;

const paymentView = Immutable.Map({
  payPaymentsWidth: window.localStorage.getItem('payPaymentsWidth'),
  payInfoBarWidth: window.localStorage.getItem('payInfoBarWidth'),
});

export function configureStore(router: ReturnType<typeof createBrowserRouter>) {
  // Routes render as soon as the session is restored, before the navigator saga
  // dispatches the first LOCATION_CHANGE, so the company of the initial URL is
  // known up front; otherwise the first renders resolve permissions with no
  // company. Only companyId is preloaded: a preloaded location would make the
  // navigator see the first LOCATION_CHANGE as no change and skip the saga of
  // the initial route.
  const initialState = {
    routing: RouterFactory({ companyId: companyIdOf(router.routes, router.state.location) }),
  };

  // add sagaMonitor if reactotron is enabled

  const sagaMiddlewareConfig = {
    onError: sagaUncaughtErrorHandler,
    ...(reactotronEnabled ? { sagaMonitor: Reactotron.createSagaMonitor() } : {}),
  };

  const sagaMiddleware = createSagaMiddleware(sagaMiddlewareConfig);

  const devEnhancers = __DEV__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : undefined;
  const composeEnhancers = typeof devEnhancers === 'function' ? devEnhancers : compose;

  // using Reactotron's createStore if Reactotron is enabled
  const storeFactory =
    reactotronEnabled && typeof Reactotron.createStore === 'function' ? Reactotron.createStore : createStore;

  const store = storeFactory(
    combineReducers({
      ...require('@/domain/env').reducer({
        linkedSidebarWidth: linkedSidebarWidth ? parseInt(linkedSidebarWidth, 10) : 0,
        paymentView,
        ...env,
        ...JSON.parse(locales),
      }),
      ...require('@/domain/router').reducer,
      ...require('@/domain/companies').reducer,
      ...require('@/domain/documents').reducer,
      ...require('@/domain/approvals').reducer,
      ...require('@/domain/textract').reducer,
      ...require('@/domain/journal').reducer,
      ...require('@/domain/settings').reducer,
      ...require('@/domain/chat').reducer,
      ...require('@/domain/organization').reducer,
      ...require('@/domain/googleSearch').reducer,
      ...require('@/domain/events').reducer,
      ...require('@/domain/dictionary').reducer,
      ...require('@/domain/messages').reducer,
      ...require('@/domain/devices').reducer,
      ...require('@/domain/categories').reducer,
      ...require('@/domain/ui').reducer,
      ...require('@/domain/agGrid').reducer,
      ...require('@/domain/jeFieldRearrangement').reducer,
      ...require('@/domain/bankFeed').reducer,
      ...require('@/domain/paySettings').reducer,
      ...require('@/domain/payCategories').reducer,
      ...require('@/domain/payments').reducer,
    }),
    initialState,
    composeEnhancers(
      applyMiddleware(
        // First, so every later middleware and every saga sees the company of the navigation.
        routeCompanyMiddleware(router.routes),
        chatMiddleware(),
        chatV2ToastMiddleware(),
        sagaMiddleware,
        routerMiddleware(router.navigate),
        deferredToastMiddleware,
      ),
    ),
  );

  subscribeRouteCompany(router, store);

  return Object.assign(store, {
    runSaga: sagaMiddleware.run,
    close: () => store.dispatch(END),
  });
}
